/**
 * CreatureCard Component
 * L4-EGE-004: Display a single creature's post-battle statistics
 *
 * Shows creature avatar (initial), name, damage dealt, survival status, and MVP indicator.
 */

import type { CreaturePerformance } from '../../utils/combat-stats';
import styles from './CreatureCard.module.css';

export interface CreatureCardProps {
  /** Performance data for this creature */
  performance: CreaturePerformance;
  /** Whether this creature is the MVP of their team */
  isMVP: boolean;
  /** Animation delay in milliseconds for staggered reveal */
  delay: number;
}

/**
 * CreatureCard Component
 *
 * Displays a single creature's post-battle statistics in the end-game roster.
 */
export function CreatureCard({ performance, isMVP, delay }: CreatureCardProps): JSX.Element {
  const { creatureName, portraitUrl, damageDealt, healingDone, survived } = performance;

  // Get first letter for avatar fallback
  const initial = creatureName.charAt(0).toUpperCase();

  // Determine primary stat to display (damage or healing if healer)
  const primaryStat = healingDone > damageDealt ? healingDone : damageDealt;
  const primaryLabel = healingDone > damageDealt ? 'healing' : 'dmg';

  // Truncate long names
  const displayName = creatureName.length > 12
    ? `${creatureName.substring(0, 12)}...`
    : creatureName;

  // Build class list
  const cardClasses = [
    styles.card,
    survived ? styles.alive : styles.dead,
    isMVP ? styles.mvp : ''
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cardClasses}
      style={{ animationDelay: `${delay}ms` }}
      title={creatureName}  // Full name on hover
      role="listitem"
      aria-label={`${creatureName}, ${primaryStat.toLocaleString()} ${primaryLabel}, ${survived ? 'survived' : 'defeated'}${isMVP ? ', MVP' : ''}`}
    >
      {/* Avatar - portrait image or fallback initial */}
      <div className={styles.avatar}>
        {portraitUrl ? (
          <img
            src={portraitUrl}
            alt={creatureName}
            className={styles.portrait}
            onError={(e) => {
              // Hide broken image, show initial fallback
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : null}
        <span
          className={styles.initial}
          style={{ display: portraitUrl ? 'none' : 'flex' }}
        >
          {initial}
        </span>
        {!survived && <span className={styles.skull} aria-hidden="true">💀</span>}
      </div>

      {/* Creature info */}
      <div className={styles.info}>
        <div className={styles.nameRow}>
          <span className={styles.name}>{displayName}</span>
          {isMVP && <span className={styles.mvpStar} aria-label="MVP">★</span>}
        </div>
        <div className={styles.stats}>
          <span className={healingDone > damageDealt ? styles.healing : styles.damage}>
            {primaryStat.toLocaleString()} {primaryLabel}
          </span>
          {survived ? (
            <span className={styles.survived} aria-label="survived">✓</span>
          ) : (
            <span className={styles.defeated} aria-label="defeated">✗</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreatureCard;
